// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-18) — the "Highest … / Lowest …" block under a
 * series in Analyze (area over a threshold, region through time, the Line
 * over time): each row names a value, the frame's date as a button that
 * plays from it, the other frames holding the same value, and where, when
 * the series knows. The arithmetic is `measure/whenExtremes.ts`; the pins'
 * own block is drawn in `dataPinsUI.ts` with the same look.
 */

import { t } from '../i18n'
import { formatNumber } from '../i18n/format'

export interface WhenRow {
  /** `max` or `min`, for the tests and the styles. */
  readonly kind: string
  readonly label: string
  readonly value: string
  readonly time: string
  /** Other frames with the same value. */
  readonly ties: number
  readonly where?: string | null
  /** Plays from that frame; without it the date is plain text. */
  readonly onSeek?: (() => void) | null
}

/** The block: a title, one row per end, and a note under them (how much was read). */
export function renderWhenBlock(title: string, rows: readonly WhenRow[], note?: string | null): HTMLElement {
  const box = document.createElement('div')
  box.className = 'analyze-when'
  box.setAttribute('role', 'group')
  box.setAttribute('aria-label', title)
  const head = document.createElement('p')
  head.className = 'analyze-when-head'
  head.textContent = title
  box.appendChild(head)
  for (const r of rows) {
    const row = document.createElement('p')
    row.className = 'analyze-when-row'
    row.dataset.when = r.kind
    const label = document.createElement('span')
    label.className = 'analyze-when-label'
    label.textContent = r.label
    const value = document.createElement('span')
    value.className = 'analyze-when-value'
    value.textContent = r.value
    row.append(label, value)
    if (r.onSeek) {
      const go = document.createElement('button')
      go.type = 'button'
      go.className = 'analyze-when-time'
      go.textContent = r.time
      go.setAttribute('aria-label', t('tools.data.when.seek', { time: r.time }))
      const seek = r.onSeek
      go.addEventListener('click', (e) => {
        e.stopPropagation()
        seek()
      })
      row.appendChild(go)
    } else {
      const time = document.createElement('span')
      time.className = 'analyze-when-time'
      time.textContent = r.time
      row.appendChild(time)
    }
    if (r.ties > 0) {
      const ties = document.createElement('span')
      ties.className = 'analyze-when-ties'
      ties.textContent = t(r.ties === 1 ? 'tools.data.when.tiesOne' : 'tools.data.when.ties', { count: formatNumber(r.ties) })
      row.appendChild(ties)
    }
    if (r.where) {
      const where = document.createElement('span')
      where.className = 'analyze-when-where'
      where.textContent = r.where
      row.appendChild(where)
    }
    box.appendChild(row)
  }
  if (note) {
    const p = document.createElement('p')
    p.className = 'analyze-when-note'
    p.textContent = note
    box.appendChild(p)
  }
  return box
}

const drawnSig = new WeakMap<HTMLElement, string>()

/**
 * Put a block in `host` when what it says changed (`sig`), else leave it:
 * the series it describes is redrawn on every frame read. A date that had
 * the focus keeps it across the rewrite. An empty build hides the host.
 */
export function fillWhenHost(host: HTMLElement, sig: string, build: () => HTMLElement | null): void {
  if (drawnSig.get(host) === sig && host.isConnected) return
  drawnSig.set(host, sig)
  const active = typeof document !== 'undefined' ? document.activeElement : null
  const kind = active && host.contains(active) ? (active as HTMLElement).closest<HTMLElement>('[data-when]')?.dataset.when : undefined
  const el = build()
  host.replaceChildren(...(el ? [el] : []))
  host.hidden = !el
  if (kind) host.querySelector<HTMLElement>(`[data-when="${kind}"] button`)?.focus()
}

/** Compass coordinates, as the pins' list prints them. */
export function compassWhere(lat: number, lon: number): string {
  // i18n-exempt: compass letters, as the pins' list and the latlng readout
  return `${formatNumber(Math.abs(lat), { maximumFractionDigits: 1 })}° ${lat >= 0 ? 'N' : 'S'}, ${formatNumber(Math.abs(lon), { maximumFractionDigits: 1 })}° ${lon >= 0 ? 'E' : 'W'}`
}
